/**
 * PURPOSE: Confirmar la creación o recreación segura de la tabla de posiciones.
 * RESPONSIBILITIES: Mostrar asignaciones faltantes y delegar la validación definitiva al backend.
 * COLLABORATORS: FasesArea.jsx y phaseToolsService.js.
 * ROUTE: winscore/src/area/FasesArea/components/PositionsCreationModal.jsx
 */
import React, { useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import { FiAlertTriangle, FiGrid } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { recreatePhasePositions } from "../../../services/phaseToolsService.js";

const PositionsCreationModal = ({ open, onClose, idevent, idsport, context, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const participants = useMemo(
    () => context?.participants || [],
    [context?.participants]
  );
  const missing = useMemo(
    () => participants.filter((participant) => !participant.idgrupo || !participant.nro_grupo),
    [participants]
  );

  const handleCreate = async () => {
    try {
      setSaving(true);
      const result = await recreatePhasePositions({ idevent, idsport });
      toast.success(`Tabla creada con ${result.created} participante(s).`);
      await onChanged?.();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "No se pudo crear la tabla de posiciones."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={saving ? undefined : onClose} sx={{ p: 2 }}>
      <Sheet className="mx-auto w-[min(96vw,620px)] rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
        <ModalClose disabled={saving} />
        <div className="pr-10">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">Fase de grupos</p>
          <h2 className="mt-1 flex items-center gap-2 text-xl font-black text-slate-900">
            <FiGrid className="text-blue-600" /> Crear tabla de posiciones
          </h2>
        </div>

        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3 text-sm font-black text-slate-800">
            <span>Participantes detectados</span>
            <span className="rounded-full bg-white px-3 py-1 text-blue-700">{participants.length}</span>
          </div>
        </div>

        {missing.length ? (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
            <div className="flex items-start gap-2">
              <FiAlertTriangle className="mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-black">Falta asignar grupo y posición</p>
                <p className="mt-1 text-xs leading-5">
                  {missing
                    .map(
                      (participant) =>
                        participant.institution?.abrev ||
                        participant.institution?.name ||
                        `Participante ${participant.idparticipant}`
                    )
                    .join(", ")}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-800">
            Todos los participantes tienen grupo y posición asignados.
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-slate-300 px-5 py-2.5 font-bold text-slate-700 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={saving || !participants.length || missing.length > 0}
            className="rounded-xl bg-blue-600 px-5 py-2.5 font-black text-white shadow-lg shadow-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Creando..." : "Crear tabla de posiciones"}
          </button>
        </div>
      </Sheet>
    </Modal>
  );
};

export default PositionsCreationModal;
